'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAuth } from '@repo/auth';
import { useOrganisationScope } from '@repo/features/organisations';
import { useAppForm, useSelector } from '@repo/form';
import { isResponseError } from '@repo/upfetch';
import { getScopeOrganisationId } from '@repo/upfetch/core';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@repo/tanstack/query';
import { useCreateCompanySheet } from './context';
import { buildAddress, emptyToUndefined, getCompanyOverviewPath, normalizeSource, normalizeWebsite } from './helpers';
import { createCompanyDefaultValues, CreateCompanySchema } from './schema';
import { useCreateCompanyVat } from './use-create-company-vat';
export const useCreateCompanyForm = () => {
  const $ = _c(41);
  const api = useApi();
  const router = useRouter();
  const {
    source
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    closeCreateCompanySheet,
    options
  } = useCreateCompanySheet();
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);
  const {
    scope
  } = useOrganisationScope();
  let t0;
  if ($[0] !== scope) {
    t0 = getScopeOrganisationId(scope);
    $[0] = scope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const scopeOrganisationId = t0;
  const companyListInvalidates = {
    invalidates: [["companies"]]
  };
  const {
    isPending: isCreatingBroker,
    mutateAsync: createBroker
  } = useMutation(api.companies.createBroker.mutationOptions({
    meta: companyListInvalidates
  }));
  const {
    isPending: isCreatingShipper,
    mutateAsync: createShipper
  } = useMutation(api.companies.createCompanyShipper.mutationOptions({
    meta: companyListInvalidates
  }));
  const {
    isPending: isCreatingHaulier,
    mutateAsync: createHaulier
  } = useMutation(api.hauliers.company.create.mutationOptions({
    meta: companyListInvalidates
  }));
  const t1 = scopeOrganisationId ?? createCompanyDefaultValues.organisation_id;
  const t2 = options?.defaultType ?? createCompanyDefaultValues.type;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...createCompanyDefaultValues,
      organisation_id: t1,
      type: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const defaultValues = t3;
  let t4;
  if ($[5] !== createBroker || $[6] !== createHaulier || $[7] !== createShipper || $[8] !== source) {
    t4 = async value => {
      const payload = {
        address: value.address,
        country: emptyToUndefined(value.country),
        email: emptyToUndefined(value.email),
        notes: emptyToUndefined(value.notes),
        organisation_id: value.organisation_id ?? undefined,
        phone: emptyToUndefined(value.phone),
        phone_country: emptyToUndefined(value.phone_country),
        registration_number: emptyToUndefined(value.registration_number),
        title: value.title.trim(),
        vat: emptyToUndefined(value.vat),
        website: normalizeWebsite(value.website)
      };
      switch (value.type) {
        case "broker":
          {
            return createBroker({
              ...payload,
              source: normalizeSource(source),
              type: "broker"
            });
          }
        case "haulier":
          {
            return createHaulier({
              ...payload,
              source: normalizeSource(source),
              type: "haulier"
            });
          }
        case "shipper":
        default:
          {
            return createShipper({
              ...payload,
              source: normalizeSource(source),
              type: "shipper"
            });
          }
      }
    };
    $[5] = createBroker;
    $[6] = createHaulier;
    $[7] = createShipper;
    $[8] = source;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const createCompanyByType = t4;
  let t5;
  if ($[10] !== closeCreateCompanySheet || $[11] !== createCompanyByType || $[12] !== queryClient || $[13] !== router) {
    t5 = async t6 => {
      const {
        value: value_0
      } = t6;
      setSubmitErrorMessage(null);
      if (value_0.type === "broker" || value_0.type === "haulier" || value_0.type === "shipper") {
        const type = value_0.type;
        const address = buildAddress({
          ...value_0.address,
          country: value_0.country
        });
        ;
        try {
          const company = await createCompanyByType({
            ...value_0,
            address,
            type
          });
          await queryClient.invalidateQueries({
            queryKey: ["/companies"]
          });
          router.push(getCompanyOverviewPath({
            id: company.id,
            type
          }));
          closeCreateCompanySheet();
        } catch (t7) {
          const error = t7;
          if (isResponseError(error)) {
            setSubmitErrorMessage(error.data.message);
            return;
          }
          throw error;
        }
      }
    };
    $[10] = closeCreateCompanySheet;
    $[11] = createCompanyByType;
    $[12] = queryClient;
    $[13] = router;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      onSubmit: CreateCompanySchema
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== defaultValues || $[17] !== t5) {
    t7 = {
      defaultValues,
      onSubmit: t5,
      validators: t6
    };
    $[16] = defaultValues;
    $[17] = t5;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const form = useAppForm(t7);
  const vat = useSelector(form.store, _temp);
  const country = useSelector(form.store, _temp2);
  const organisationId = useSelector(form.store, _temp3);
  let t8;
  if ($[19] !== country || $[20] !== form.setFieldValue || $[21] !== vat) {
    t8 = {
      country,
      setFieldValue: form.setFieldValue,
      vat
    };
    $[19] = country;
    $[20] = form.setFieldValue;
    $[21] = vat;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const {
    existingCompany,
    handleVatBlur,
    isVatLookupPending,
    vatLookupMessage
  } = useCreateCompanyVat(t8);
  const isAllOrganisationsSelected = scope.kind === "all";
  const isPending = isCreatingBroker || isCreatingHaulier || isCreatingShipper;
  const isSubmitDisabled = isPending || isVatLookupPending || Boolean(existingCompany);
  const previousScopeOrganisationId = useRef(scopeOrganisationId);
  let t10;
  let t9;
  if ($[23] !== form || $[24] !== organisationId || $[25] !== scopeOrganisationId) {
    t9 = () => {
      if (scopeOrganisationId !== undefined) {
        if (organisationId !== scopeOrganisationId) {
          form.setFieldValue("organisation_id", scopeOrganisationId);
        }
      } else {
        if (previousScopeOrganisationId.current !== undefined && organisationId !== null) {
          form.setFieldValue("organisation_id", null);
        }
      }
      previousScopeOrganisationId.current = scopeOrganisationId;
    };
    t10 = [form, organisationId, scopeOrganisationId];
    $[23] = form;
    $[24] = organisationId;
    $[25] = scopeOrganisationId;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  let t11;
  if ($[28] !== closeCreateCompanySheet || $[29] !== country || $[30] !== existingCompany || $[31] !== form || $[32] !== handleVatBlur || $[33] !== isAllOrganisationsSelected || $[34] !== isPending || $[35] !== isSubmitDisabled || $[36] !== isVatLookupPending || $[37] !== submitErrorMessage || $[38] !== vat || $[39] !== vatLookupMessage) {
    t11 = {
      AppField: form.AppField,
      AppForm: form.AppForm,
      closeCreateCompanySheet,
      country,
      existingCompany,
      form,
      handleSubmit: form.handleSubmit,
      handleVatBlur,
      isAllOrganisationsSelected,
      isPending,
      isSubmitDisabled,
      isVatLookupPending,
      setFieldValue: form.setFieldValue,
      submitErrorMessage,
      vat,
      vatLookupMessage
    };
    $[28] = closeCreateCompanySheet;
    $[29] = country;
    $[30] = existingCompany;
    $[31] = form;
    $[32] = handleVatBlur;
    $[33] = isAllOrganisationsSelected;
    $[34] = isPending;
    $[35] = isSubmitDisabled;
    $[36] = isVatLookupPending;
    $[37] = submitErrorMessage;
    $[38] = vat;
    $[39] = vatLookupMessage;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
};
function _temp(state) {
  return state.values.vat;
}
function _temp2(state_0) {
  return state_0.values.country;
}
function _temp3(state_1) {
  return state_1.values.organisation_id;
}